/**
 * Valor total de um contrato — e por que ele nao e' so' `valorFinanciado`.
 *
 * Contrato de tombamento (`origem: legado`) nasce com `valorFinanciado: 0` DE
 * PROPOSITO, em `portal-banco/store.ts:registrarLegado`: "A Atlas nao financiou
 * nada aqui e nao conhece a taxa". Zero ali nao afirma "zero reais", afirma
 * "nao sei" — e o dominio tem regra explicita de que desconhecido e zero nao
 * podem virar o mesmo pixel (`dadosFinanceirosConhecidos`).
 *
 * Duas telas liam esse zero de jeitos diferentes e as duas erravam:
 *
 *   /averbadora/adf        `valorFinanciado ?? parcela * total` — o `??` so' cai
 *                          no fallback com null/undefined, entao o zero passava
 *                          direto e a coluna exibia R$ 0,00. Como TODO contrato
 *                          vindo de carga inicial nasce legado, a folha inteira
 *                          zerava.
 *   /averbadora/contratos  `—` puro. Honesto, mas um traco solto numa coluna de
 *                          dinheiro le como celula quebrada, e o tooltip que
 *                          explicava so' aparecia no hover.
 *
 * O que se exibe no lugar: a SOMA DAS PARCELAS. Ela e' conhecida (veio da
 * folha), e' o que efetivamente sai do salario, e nao inventa um principal que
 * a Atlas nunca emprestou.
 *
 * ## Dois numeros, dois nomes (feature 012, 11/09/2026)
 *
 * A soma das parcelas chegou a ocupar a coluna "Valor", com um "12x" ao lado.
 * O cliente leu a tela e concluiu o obvio: e' o TOTAL A PAGAR com juros, na
 * mesma coluna em que o contrato da Atlas mostra o PRINCIPAL. Comparar as
 * linhas perdia o sentido. Desde entao sao duas grandezas com nome proprio:
 *
 *   **valor solicitado**  o principal — `valorSolicitado()`, `null` quando a
 *                         Atlas nao o conhece (a tela mostra "—" e o selo
 *                         "legado"; NUNCA R$ 0,00, nunca a soma).
 *   **total em folha**    parcela × total de parcelas — `totalEmFolha()`, sempre
 *                         conhecido, sempre com rotulo proprio.
 *
 * `valorTotalDoContrato`/`ehSomaDeParcelas` continuam existindo para o
 * TOTALIZADOR ("quanto vale este recorte"), que precisa de um numero por linha.
 * Coluna de tabela nao usa mais os dois: coluna e' o lugar em que a grandeza
 * misturada enganou.
 *
 * O que deixou de ser impossivel: a taxa a.m. do legado. Quem tem a cedula — o
 * banco credor, ou a averbadora como excecao — informa principal e taxa, e a
 * API devolve `dadosFinanceirosConhecidos: true` (ver `dadosInformados` em
 * @atlas/domain). Sem informe, continua `—`.
 *
 * ## Por que mora em `@atlas/types` desde a feature 007
 *
 * Nasceu em `apps/web/src/lib/valor-contrato.ts`, e mudou de casa pelo mesmo
 * motivo que `busca.ts` mudou na 002: quando a listagem de contratos passou a
 * paginar no servidor, o TOTALIZADOR do cabecalho teve de ser calculado la —
 * ele fala do recorte inteiro, e o servidor e o unico lado que ainda o tem.
 *
 * Calcular a soma na API com uma copia desta regra criaria duas definicoes de
 * "valor total", e a divergencia entre elas nao daria erro: daria um KPI com um
 * numero e uma tabela com outro, os dois plausiveis. E exatamente o defeito que
 * este arquivo foi escrito para consertar, so que uma camada acima.
 */
/** true quando a Atlas conhece o principal financiado (contrato com CCB). */
export function temValorFinanciado(c) {
    // A flag da API so' pode NEGAR. O caso positivo ainda exige numero de
    // verdade — contrato marcado como conhecido mas com principal zerado e'
    // dado furado, e exibir "R$ 0,00" por causa dele repetiria o bug original.
    if (c.dadosFinanceirosConhecidos === false)
        return false;
    return typeof c.valorFinanciado === "number" && c.valorFinanciado > 0;
}
/** Valor total: principal quando conhecido, soma das parcelas quando nao. */
export function valorTotalDoContrato(c) {
    return temValorFinanciado(c) ? c.valorFinanciado : c.valorParcela * c.totalParcelas;
}
/** true quando o numero exibido e' soma de parcelas, nao principal financiado. */
export function ehSomaDeParcelas(c) {
    return !temValorFinanciado(c);
}
/**
 * O PRINCIPAL, ou `null` quando a Atlas nao o conhece.
 *
 * `null` e' a resposta certa para o legado sem informe, e a tela precisa
 * traduzi-la em "—" com o selo: qualquer numero aqui — zero ou a soma das
 * parcelas — e' a afirmacao falsa que este modulo existe para impedir.
 */
export function valorSolicitado(c) {
    return temValorFinanciado(c) ? c.valorFinanciado : null;
}
/**
 * O que sai da folha ao longo do contrato: parcela × total de parcelas.
 *
 * Sempre conhecido — veio da folha — e sempre exibido sob ESTE rotulo, nunca
 * como "Valor". Vale para contrato da Atlas tambem: la' e' o total a pagar, e a
 * diferenca para o principal e' o juro.
 */
export function totalEmFolha(c) {
    return Math.round(c.valorParcela * c.totalParcelas * 100) / 100;
}
/**
 * Situacao TERMINAL NEGATIVA — a operacao morreu e nao entra em soma de dinheiro.
 *
 * Mora aqui, ao lado de `valorTotalDoContrato`, porque so existe para decidir o
 * que entra nos totalizadores: antes desta regra um contrato cancelado de
 * R$ 100 mil inflava "Valor financiado" e contradizia o KPI de conversao.
 *
 * Veio de `apps/web/src/routes/averbadora/contratos.tsx` na feature 007, pelo
 * mesmo motivo que `valorTotalDoContrato` mudou de casa: com a listagem
 * paginada no servidor, os totais passaram a ser calculados la — e o servidor
 * precisa da MESMA nocao de "operacao viva" que a tela tinha. Duas listas de
 * palavras dariam dois KPIs plausiveis e diferentes.
 *
 * Casa por SUBSTRING de proposito: a situacao e string livre vinda de origens
 * diferentes (banco, folha, conciliacao) e aparece como "recusado",
 * "recusada", "reprovado", "estornada". Uma lista de valores exatos deixaria
 * passar a variante que ninguem previu, e o sintoma seria um total maior sem
 * erro nenhum.
 */
export function situacaoEhTerminal(situacao) {
    const t = (situacao || "").toLowerCase();
    if (t === "expirado" || t === "cancelado" || t === "quitado")
        return true;
    return (t.includes("recus") || t.includes("reprov") || t.includes("rejeit") ||
        t.includes("negad") || t.includes("estorn"));
}
/** Soma o valor total de um lote — o agregado de cabecalho das telas. */
export function somarValorTotal(cs) {
    return cs.reduce((s, c) => s + valorTotalDoContrato(c), 0);
}
//# sourceMappingURL=valor-contrato.js.map